import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, signal, computed, Inject, Injector, makeEnvironmentProviders, APP_INITIALIZER } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { of, lastValueFrom } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { tap, map, catchError } from 'rxjs/operators';

const APP_SETTINGS = new InjectionToken('APP_SETTINGS');
const IAUTH_SETTINGS = new InjectionToken('IAUTH_SETTINGS');
const APP_ENVIRONMENT = new InjectionToken('APP_ENVIRONMENT');
const APP_PROVIDERS_ARRAY = new InjectionToken('APP_PROVIDERS_ARRAY');
const APP_SETTINGS_MANIFEST_URL = new InjectionToken('APP_SETTINGS_MANIFEST_URL');

class SettingsDataService {
    constructor(handler) {
        this.APP_SETTINGS_MANIFEST_URL = inject(APP_SETTINGS_MANIFEST_URL);
        this.http = new HttpClient(handler);
    }
    getAppSettings() {
        return this.http.get(this.APP_SETTINGS_MANIFEST_URL);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.6", ngImport: i0, type: SettingsDataService, deps: [{ token: i1.HttpBackend }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.6", ngImport: i0, type: SettingsDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.6", ngImport: i0, type: SettingsDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.HttpBackend }] });

const initialState = {
    settings: {
        isServed: false,
        api_url: '',
        api_docs: '',
        auth_settings: {},
        culture: '',
        isTemplate: false,
        production: false,
        version: ''
    },
};
class SettingsFacadeService {
    #settingsDataService;
    #environment;
    #state;
    constructor(tokenAppsettings, appProvidersArray) {
        this.tokenAppsettings = tokenAppsettings;
        this.appProvidersArray = appProvidersArray;
        this.#settingsDataService = inject(SettingsDataService);
        this.#environment = inject(APP_ENVIRONMENT);
        this.#state = signal(initialState);
        this.settings = computed(() => this.#state().settings);
    }
    getAppDependencies() {
        return this.appProvidersArray?.dependencies;
    }
    loadSettings() {
        return this.#settingsDataService.getAppSettings().pipe(tap((settings) => console.log(settings)), map((settings) => {
            let runtimeSettings = merge(cloneDeep(this.#environment), settings);
            // Assign settings directly to the appSettings object
            Object.assign(this.tokenAppsettings, runtimeSettings);
            // Update the state
            this.#state.set({ settings: this.tokenAppsettings });
            return true; // Indicate successful loading
        }), catchError((error) => {
            // Log the error and provide fallback behavior
            console.error('Failed to load settings:', error);
            return of(false); // Return false to indicate failure
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.6", ngImport: i0, type: SettingsFacadeService, deps: [{ token: APP_SETTINGS }, { token: APP_PROVIDERS_ARRAY }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.6", ngImport: i0, type: SettingsFacadeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.6", ngImport: i0, type: SettingsFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_SETTINGS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_PROVIDERS_ARRAY]
                }] }] });

function initializeAppSettings(appSettingsFacade) {
    return async () => {
        // Wait for settings to load
        const success = await lastValueFrom(appSettingsFacade.loadSettings());
        if (success) {
            const dependencies = appSettingsFacade.getAppDependencies();
            // Create a child injector with the dependencies
            Injector.create({ providers: dependencies });
        }
        else {
            console.error('Failed to initialize app settings.');
            throw new Error('App settings could not be loaded.');
        }
    };
}
function provideAppSettings(config = {}) {
    return makeEnvironmentProviders([
        {
            provide: APP_SETTINGS_MANIFEST_URL,
            useValue: '/app-settings.manifest.json'
        },
        {
            provide: APP_PROVIDERS_ARRAY,
            useValue: config.dependencies || []
        },
        {
            provide: APP_SETTINGS,
            useValue: {} // Provide an empty object initially
        },
        {
            provide: APP_ENVIRONMENT,
            useValue: {}
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppSettings,
            deps: [SettingsFacadeService], // Add Injector to deps
            multi: true,
        },
        {
            provide: IAUTH_SETTINGS,
            useFactory: (runtimeSettings) => runtimeSettings.auth_settings,
            deps: [APP_SETTINGS],
        }
    ]);
}

/*
 * Public API Surface of ng-conf
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_ENVIRONMENT, APP_PROVIDERS_ARRAY, APP_SETTINGS, APP_SETTINGS_MANIFEST_URL, IAUTH_SETTINGS, SettingsDataService, SettingsFacadeService, initializeAppSettings, provideAppSettings };
